import { memo, useCallback, useEffect, useMemo, useReducer } from 'react';
import type {
  ReservationTimeDisplay,
  ReservationTimeDisplayUnit,
} from 'types/reservationDisplaySettings.type';
import { Button, TEInput } from '@timeedit/ui-components';
import { Modal, Typography } from '@timeedit/ui-components/antd';
import { LabelWrapper } from '@timeedit/ui-components/components/InputComponent/Input';

const UNIT_DISPLAY: Record<ReservationTimeDisplayUnit, string> = {
  DAY: 'days',
  HOUR: 'hours',
  MINUTE: 'minutes',
};

// Improve: memoize unit options once
const UNIT_OPTIONS = Object.entries(UNIT_DISPLAY).map(([key, value]) => ({
  label: value,
  value: key,
}));

type PublishingConfigProps = {
  enabled?: boolean;
  value?: number;
  unit?: ReservationTimeDisplayUnit;
  onClear: () => void;
  onChangeEnabled: (enabled: boolean) => void;
  onChangeValue: (newValue: number) => void;
  onChangeUnit: (newUnit: ReservationTimeDisplayUnit) => void;
};

const getPublishingMessage = (
  enabled: boolean,
  timeDisplay: ReservationTimeDisplay | undefined | null
) => {
  if (!enabled) {
    return 'Not published';
  }

  if (
    timeDisplay == null ||
    timeDisplay.unit == null ||
    timeDisplay.value == null
  ) {
    return 'Immediately';
  }

  return `${timeDisplay.value} ${UNIT_DISPLAY[timeDisplay.unit]} before`;
};

export const PublishingConfigShortMessage = memo(
  (props: {
    enabled: boolean;
    timeDisplay: ReservationTimeDisplay | undefined | null;
    onOpenModal?: (open: boolean) => void;
  }) => {
    return (
      <div className="reservation-display-settings-publish-modal-button">
        <Typography.Text>
          {getPublishingMessage(props.enabled, props.timeDisplay)}
        </Typography.Text>
        <Button
          type="default"
          size="small"
          onClick={() => props.onOpenModal?.(true)}
        >
          Edit
        </Button>
      </div>
    );
  }
);

const PublishingConfig = memo(
  ({
    enabled,
    value,
    unit,
    onClear,
    onChangeEnabled,
    onChangeValue,
    onChangeUnit,
  }: PublishingConfigProps) => {
    const onValueChange = useCallback(
      (newVal: number | string | null) => {
        if ((newVal == null || newVal === '') && unit == null) {
          onClear();
          return;
        }
        if (newVal == null || newVal === '') return; // ignore empty entry but keep existing
        const parsed =
          typeof newVal === 'string' ? Number.parseInt(newVal, 10) : newVal;
        onChangeValue(Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
      },
      [onClear, onChangeValue, unit]
    );

    const onUnitChange = useCallback(
      (newUnit: ReservationTimeDisplayUnit | null) => {
        if (newUnit == null && (value == null || value === undefined)) {
          onClear();
          return;
        }
        if (newUnit) onChangeUnit(newUnit);
      },
      [onClear, onChangeUnit, value]
    );

    return (
      <div className="reservation-display-settings-publish-config-container">
        <TEInput.Checkbox
          checked={enabled}
          label="Enabled"
          onChange={(e) => onChangeEnabled(e.target.checked)}
        />
        <div className="reservation-display-settings-publish-config-inner">
          <LabelWrapper label="Publish">
            <div className="reservation-display-settings-publish-config-input-container">
              <div className="reservation-display-settings-publish-config-input-button-container">
                <div className="reservation-display-settings-publish-config-input-container">
                  <TEInput.InputNumber
                    disabled={!enabled}
                    value={value}
                    type="number"
                    onChange={onValueChange}
                    min={1}
                  />
                  <TEInput.Select
                    disabled={!enabled}
                    value={unit}
                    options={UNIT_OPTIONS}
                    onChange={onUnitChange}
                    allowClear={false}
                  />
                </div>
                <Button
                  disabled={!enabled || (value == null && unit == null)}
                  size="small"
                  onClick={onClear}
                >
                  Clear
                </Button>
              </div>
              before reservation start time
            </div>
          </LabelWrapper>
        </div>
      </div>
    );
  }
);

interface PublishingConfigurationModalProps {
  title: string;
  isOpen: boolean;
  record: { enabled: boolean; timeDisplay?: ReservationTimeDisplay };
  // index undefined indicates a single, non-array field (seat/location)
  onArrayFieldChange: (
    dataPath: string
  ) => (index: number | undefined, key: string, value: any) => void;
  index?: number;
  path: string; // e.g., 'seat', 'location', 'fields', 'information'
  onCancel?: (open: boolean) => void;
}

const buildTimeDisplay = (
  existing: ReservationTimeDisplay | null | undefined,
  patch: Partial<ReservationTimeDisplay>
): ReservationTimeDisplay | null => {
  const next = {
    ...(existing ?? {}),
    ...patch,
  } as Partial<ReservationTimeDisplay>;
  if (next.unit != null && next.value != null) {
    return { unit: next.unit, value: next.value } as ReservationTimeDisplay;
  }
  return null; // treat incomplete as null for external consumption
};

// Update reducer to keep possibly partial internally but convert on read
interface ModalRecordState {
  enabled: boolean;
  timeDisplay: ReservationTimeDisplay | null; // external-consumable form
  draft: Partial<ReservationTimeDisplay> | null; // internal draft while editing
}

type ModalRecordAction =
  | { type: 'SET_ENABLED'; payload: boolean }
  | { type: 'SET_VALUE'; payload: number }
  | { type: 'SET_UNIT'; payload: ReservationTimeDisplayUnit }
  | { type: 'CLEAR' }
  | {
      type: 'RESET';
      payload: {
        enabled: boolean;
        timeDisplay: ReservationTimeDisplay | null;
      };
    };

const modalRecordReducer = (
  state: ModalRecordState,
  action: ModalRecordAction
): ModalRecordState => {
  switch (action.type) {
    case 'SET_ENABLED': {
      if (!action.payload) {
        return { ...state, enabled: false, timeDisplay: null, draft: null };
      }
      return { ...state, enabled: true };
    }
    case 'SET_VALUE': {
      const draft = {
        ...(state.draft ?? state.timeDisplay ?? {}),
        value: action.payload,
      } as Partial<ReservationTimeDisplay>;
      return {
        ...state,
        draft,
        timeDisplay: buildTimeDisplay(state.timeDisplay, draft),
      };
    }
    case 'SET_UNIT': {
      const draft = {
        ...(state.draft ?? state.timeDisplay ?? {}),
        unit: action.payload,
      } as Partial<ReservationTimeDisplay>;
      return {
        ...state,
        draft,
        timeDisplay: buildTimeDisplay(state.timeDisplay, draft),
      };
    }
    case 'CLEAR':
      return { ...state, timeDisplay: null, draft: null, enabled: true };
    case 'RESET':
      return {
        enabled: action.payload.enabled,
        timeDisplay: action.payload.timeDisplay,
        draft: action.payload.timeDisplay ?? null,
      };
    default:
      return state;
  }
};

// Adjust hook usage inside modal component (replace previous reducer initialization & usage accordingly)
export const PublishingConfigurationModal = memo(
  function PublishingConfigurationModal(
    props: PublishingConfigurationModalProps
  ) {
    const initial = props.record;
    const [state, dispatchBase] = useReducer(modalRecordReducer, {
      enabled: initial.enabled,
      timeDisplay: initial.timeDisplay ?? null,
      draft: initial.timeDisplay ?? null,
    } as ModalRecordState);

    // Typed dispatch wrapper
    const dispatch = useCallback(
      (action: ModalRecordAction) => dispatchBase(action),
      []
    );

    useEffect(() => {
      dispatch({
        type: 'RESET',
        payload: {
          enabled: props.record.enabled,
          timeDisplay: props.record.timeDisplay ?? null,
        },
      });
    }, [props.record, dispatch]);

    const arrayUpdater = useMemo(
      () => props.onArrayFieldChange(props.path),
      [props.onArrayFieldChange, props.path]
    );

    const onOk = useCallback(() => {
      arrayUpdater(props.index, 'enabled', state.enabled);
      arrayUpdater(props.index, 'timeDisplay', state.timeDisplay ?? undefined);
      props.onCancel?.(false);
    }, [
      arrayUpdater,
      props.index,
      state.enabled,
      state.timeDisplay,
      props.onCancel,
    ]);

    const footerModal = (
      <>
        <Button
          key="cancel"
          size="small"
          onClick={() => props.onCancel?.(false)}
        >
          Cancel
        </Button>
        <Button key="save" type="primary" size="small" onClick={onOk}>
          Save
        </Button>
      </>
    );

    return (
      <Modal
        title={props.title}
        open={props.isOpen}
        onCancel={() => props.onCancel?.(false)}
        closable
        footer={footerModal}
      >
        <div className="reservation-display-settings-publish-modal-container">
          <PublishingConfig
            enabled={state.enabled}
            value={state.timeDisplay?.value}
            unit={state.timeDisplay?.unit}
            onClear={() => dispatch({ type: 'CLEAR' })}
            onChangeEnabled={(val) =>
              dispatch({ type: 'SET_ENABLED', payload: val })
            }
            onChangeValue={(val) =>
              dispatch({ type: 'SET_VALUE', payload: val })
            }
            onChangeUnit={(newUnit) =>
              dispatch({ type: 'SET_UNIT', payload: newUnit })
            }
          />
        </div>
      </Modal>
    );
  }
);
